/* Resolving a position's size ceiling.
 *
 * The ceilings were nine constants and a union type built from their keys, so
 * the compiler answered every one of these questions and the set could never
 * change. They are stored records now, which means an id can name a band that
 * is not there any more — the user deleted it, or a book arrived from a device
 * that never had it.
 *
 * That state has to be a first-class answer rather than something each caller
 * improvises, because the improvisations are all wrong in the same direction.
 * `LIMITS.positionCaps[id]` on a missing key is `undefined`, which reads as
 * "no ceiling" and silently un-caps the position; a `?? 0` reads as a ceiling
 * of nothing and puts every position past its trim multiple at once. The
 * honest answer is null, and the screens say which kind of null it is:
 * **no ceiling was ever set** is a different fact from **the ceiling it names
 * is gone**.
 *
 * Sorted by percentage wherever they are listed. A ceiling list reads as a
 * ladder from the largest allowance down, and that order follows from the
 * number already on the record — a stored hand ordering would be one more
 * thing to keep right for a reading it cannot improve. */

import type { CapBandRecord, CapKind } from '../types';

/** Largest ceiling first, then alphabetically so equal ones do not swap about
    between renders. */
export function sortBands(bands: CapBandRecord[]): CapBandRecord[] {
  return [...bands].sort((a, b) => b.pct - a.pct || a.name.localeCompare(b.name));
}

export function bandById(
  bands: CapBandRecord[], id: CapKind | null | undefined,
): CapBandRecord | null {
  if (!id) return null;
  return bands.find((b) => b.id === id) ?? null;
}

/** The ceiling as a percentage, or null. Null both when nothing is assigned and
    when what is assigned no longer exists — the two are told apart by whether
    the assignment itself is there, not by this. */
export function bandPct(bands: CapBandRecord[], id: CapKind | null | undefined): number | null {
  const band = bandById(bands, id);
  return band ? band.pct : null;
}

/** An id from a typed name. Prefixed differently from `idFromName`'s broker
    fallback so a band never gets an id that reads as a broker's, and lowercase
    with underscores so it can never collide with one of the nine camelCase
    originals. */
export function bandIdFromName(name: string): string {
  const slug = name.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
  /* A name of nothing but emoji or punctuation reduces to an empty string, and
     an empty id is a key that overwrites whatever else has one. */
  return slug === '' ? `ceiling_${name.trim().length}_${[...name.trim()].map((c) => c.codePointAt(0)! % 36).join('').slice(0, 8)}` : slug;
}

/** Whether adding this name would land on a band that already exists. `put`
    overwrites by key, so an unguarded add silently moves every position off one
    ceiling and on to another. */
export function bandNameTaken(bands: CapBandRecord[], name: string): boolean {
  const id = bandIdFromName(name);
  return bands.some((b) => b.id === id);
}
